import { Injectable, inject } from '@angular/core';
import { ResourceFacade } from '~app/app/~store/facades/resource.facade';
import { UserService } from './user.service';
import { SnackbarService } from './snackbar.service';
import { DialogService } from './dialog.service';
import { User } from '~core/models/auth.models';
import { Observable, filter, tap } from 'rxjs';
import { DeleteConfirmComponent } from '../components/modals/delete-confirm/delete-confirm.component';
import { AdminCreationFormComponent } from '~app/app/settings/modals/admin-creation-form/admin-creation-form.component';

@Injectable()
export class UserManagementService {
  private resourceFacade = inject(ResourceFacade);
  private userService = inject(UserService);

  constructor(
    private snackBar: SnackbarService,
    private dialogService: DialogService
  ) {}

  editUser(user?: User): Observable<unknown> {
    const dialogRef = this.dialogService.open({
      component: AdminCreationFormComponent,
      config: {
        panelClass: ['mat-dialog-custom-full-screen'],
        disableClose: true,
        height: '100vh',
        width: '100vw',
        maxWidth: '100vw'
      }
    });
    if (user) {
      dialogRef.componentInstance.user = user;
    }
    return dialogRef.afterClosed().pipe(
      filter(Boolean),
      tap((updatedUser: User) => {
        if (user) {
          this.resourceFacade.patchItem({ item: user, newData: updatedUser });
          this.snackBar.open({
            text: 'Utilisateur mis à jour avec succès',
            type: 'success'
          });
        }
      })
    );
  }

  delete(user: User): Observable<unknown> {
    const delete$ = this.userService.delete(user.id);
    const dialogRef = this.dialogService.open({
      component: DeleteConfirmComponent,
      config: {
        panelClass: ['mat-dialog-custom'],
        disableClose: true
      }
    });

    dialogRef.componentInstance.removeObservable$ = delete$;
    dialogRef.componentInstance.wordEntry = user.username;
    dialogRef.componentInstance.title =
      'Voulez-vous supprimer cet utilisateur ?';

    return dialogRef.afterClosed().pipe(
      filter(Boolean),
      tap({
        next: () => {
          this.resourceFacade.getData({ refresh: true });
          this.snackBar.open({
            text: 'Utilisateur supprimé',
            type: 'success'
          });
        }
      })
    );
  }

  lock(user: User): Observable<User> {
    const newData = {
      locked: true
    };
    return this.userService.update(user.id, newData).pipe(
      tap({
        next: () => {
          this.resourceFacade.patchItem({
            item: user,
            newData
          });
          this.snackBar.open({
            text: 'Utilisateur bloqué',
            type: 'success'
          });
        }
      })
    );
  }

  unLock(user: User): Observable<unknown> {
    const delete$ = this.userService.update(user.id, {
      locked: false
    });
    const dialogRef = this.dialogService.open({
      component: DeleteConfirmComponent,
      config: {
        panelClass: ['mat-dialog-custom'],
        disableClose: true
      }
    });

    dialogRef.componentInstance.removeObservable$ = delete$;
    dialogRef.componentInstance.title =
      'Voulez-vous débloquer cet Utilisateur ?';
    dialogRef.componentInstance.instruction = `Veuillez saisir le texte ci-dessous pour confirmer`;
    dialogRef.componentInstance.wordEntry = 'Je confirme';
    dialogRef.componentInstance.action = 'Débloquer';

    return dialogRef.afterClosed().pipe(
      filter(Boolean),
      tap({
        next: () => {
          this.resourceFacade.patchItem({
            item: user,
            newData: {
              locked: false
            }
          });
          this.snackBar.open({
            text: 'Utilisateur débloqué',
            type: 'success'
          });
        }
      })
    );
  }

  toggleLockState(user: User): Observable<unknown> {
    return user.locked ? this.unLock(user) : this.lock(user);
  }
}
